'use client';
import React from 'react';
import { DrawerHeader } from '@repo/ui/src/modules/UI';
import { classNames } from '@repo/ui/src/utils/classNames';
import CartBody from '@repo/ui/src/components/Cart/CartBody';
import CartFooter from '@repo/ui/src/components/Cart/CartFooter';
import type { CartLineItem, CartTotals, CartSummaryExtra } from '@repo/ui/src/types/cart';

interface CartProps {
  isLoading: boolean;
  lineItems: Array<CartLineItem>;
  totals: CartTotals;
  outOfStockIds?: Array<string>;
  updatingItemId?: string;
  disabled?: boolean;
  isCheckingOut?: boolean;
  onQuantityChange: (id: string, newQuantity: number) => void;
  onRemove: (id: string) => void;
  onCheckout: () => void;
  /**
   * When set, replaces the entire drawer body (incl. DrawerHeader and footer).
   * Used by Traeger for the auth step.
   */
  fullBodyOverride?: React.ReactNode;
  /**
   * Content rendered between DrawerHeader and CartBody (e.g. error states,
   * logged-out messaging). Shown only when fullBodyOverride is not set.
   */
  bodyPrefixSlot?: React.ReactNode;
  /** Extra items rendered below standard line items (e.g. Traeger gift card rows). */
  additionalItemsSlot?: React.ReactNode;
  promotionalNode?: React.ReactNode;
  extraSummaryLines?: Array<CartSummaryExtra>;
  title?: string;
  emptyMessage?: string;
  estimatedTotalLabel?: string;
  checkoutLabel?: string;
  loadingNode?: React.ReactNode;
  emptyCtaNode?: React.ReactNode;
  locale?: string;
  // i18n forwarded to CartBody/CartLineItem
  outOfStockMessage?: string;
  removeLabel?: string;
  outOfStockLabel?: string;
  quantityLabel?: string;
  subtotalLabel?: string;
  shippingLabel?: string;
  shippingFreeLabel?: string;
  taxLabel?: string;
  taxFreeLabel?: string;
}

const Cart = ({
  isLoading,
  lineItems,
  totals,
  outOfStockIds,
  updatingItemId,
  disabled,
  isCheckingOut,
  onQuantityChange,
  onRemove,
  onCheckout,
  fullBodyOverride,
  bodyPrefixSlot,
  additionalItemsSlot,
  promotionalNode,
  extraSummaryLines,
  title = 'Your Cart',
  emptyMessage,
  estimatedTotalLabel,
  checkoutLabel,
  loadingNode,
  emptyCtaNode,
  locale = 'en-US',
  outOfStockMessage,
  removeLabel,
  outOfStockLabel,
  quantityLabel,
  subtotalLabel,
  shippingLabel,
  shippingFreeLabel,
  taxLabel,
  taxFreeLabel,
}: CartProps) => {
  const isEmpty = !totals.totalItems;

  return (
    <div className="pb-28 grid grid-cols-1 justify-center min-h-screen justify-items-stretch grid-rows-[1fr_auto] md:items-stretch md:grid-areas-cartDrawer md:grid-cols-[auto_auto] md:pb-0">
      <div className="full flex flex-col flex-grow sm:w-[600px] md:grid-in-b">
        {fullBodyOverride ?? (
          <>
            <DrawerHeader title={title} />
            <div className="flex justify-between flex-col">
              <div className="px-6 mb-5 sm:px-16 mx-auto max-w-3xl w-full">
                {bodyPrefixSlot}
                <hr className="hidden sm:block w-full border-warm-200" />
                <CartBody
                  lineItems={lineItems}
                  isLoading={isLoading}
                  isEmpty={isEmpty}
                  outOfStockIds={outOfStockIds}
                  updatingItemId={updatingItemId}
                  onQuantityChange={onQuantityChange}
                  onRemove={onRemove}
                  additionalItemsSlot={additionalItemsSlot}
                  promotionalNode={promotionalNode}
                  extraSummaryLines={extraSummaryLines}
                  totals={totals}
                  loadingNode={loadingNode}
                  emptyMessage={emptyMessage}
                  outOfStockMessage={outOfStockMessage}
                  removeLabel={removeLabel}
                  outOfStockLabel={outOfStockLabel}
                  quantityLabel={quantityLabel}
                  subtotalLabel={subtotalLabel}
                  shippingLabel={shippingLabel}
                  shippingFreeLabel={shippingFreeLabel}
                  taxLabel={taxLabel}
                  taxFreeLabel={taxFreeLabel}
                  currency={totals.currency}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {!fullBodyOverride && (
        <div className="md:grid-in-c md:sticky md:w-auto fixed w-full bottom-0 sm:w-[600px]">
          <div className={classNames('z-10 bg-white relative shadow-fixedHeader')}>
            <div className="mx-auto max-w-3xl p-6 sm:px-16">
              <CartFooter
                isLoading={isLoading}
                isEmpty={isEmpty}
                total={totals.total}
                currency={totals.currency}
                locale={locale}
                disabled={disabled}
                isCheckingOut={isCheckingOut}
                onCheckout={onCheckout}
                inDrawer
                loadingNode={loadingNode}
                emptyCtaNode={emptyCtaNode}
                estimatedTotalLabel={estimatedTotalLabel}
                checkoutLabel={checkoutLabel}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
